import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageview, trackClickToCall, trackAttorneyContactClicked } from "../lib/analytics";

/**
 * Mounted once near the app root. Two responsibilities:
 *  1) Fire GA4 page_view on every React Router navigation.
 *  2) Globally delegate clicks on tel: links and attorney-contact CTAs to
 *     the matching GA4 events — no per-component wiring required.
 */
export default function AnalyticsRouteTracker() {
  const location = useLocation();

  // 1) Pageviews on route change.
  useEffect(() => {
    const timer = setTimeout(() => {
      trackPageview(location.pathname + location.search, document.title);
    }, 0);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  // 2) Global click delegation for tel: and attorney-contact links.
  useEffect(() => {
    const onClick = (e) => {
      const anchor = e.target.closest && e.target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (href.startsWith("tel:")) {
        // Strip the tel: prefix and any non-digits for a clean phone_number param.
        const phone = href.replace(/^tel:/, "").replace(/[^\d+]/g, "");
        const source =
          anchor.getAttribute("data-testid") ||
          anchor.dataset.source ||
          "site";
        trackClickToCall(phone, source);
      }
      if (anchor.dataset.attorneyContact === "true" || href.includes("attorney")) {
        // Only fire attorney_contact_clicked when intentionally tagged or anchor
        // links to an attorney-related route; the dataset attribute is the
        // explicit opt-in for future CMS content.
        if (anchor.dataset.attorneyContact === "true") {
          trackAttorneyContactClicked(
            anchor.dataset.source || "anchor",
            { href },
          );
        }
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}
